import type { BaseElement } from 'slate';

import type { CheckboxOptionFieldElement } from './CheckboxOptionFieldElement';
import type { ParagraphElement } from './ParagraphElement';
import type {
  AlineDescendant,
  AlineText,
  BlockInstructionFieldElement,
  BookmarkMarkerElement,
  BreakElement,
  CannedFieldElement,
  CheckboxElement,
  CrossReferenceElement,
  DocumentElement,
  DrawingElement,
  DynamicBlockElement,
  DynamicInlineElement,
  FooterElement,
  FootersElement,
  HeaderElement,
  HeadersElement,
  InlineInstructionFieldElement,
  InputFieldElement,
  ListElement,
  SuggestedEdit,
  SuggestedLinkEdit,
  SuggestionMarkerElement,
  TabElement,
  TableCellElement,
  TableElement,
  TableRowElement,
  TOCElement,
} from '../../internal';

export type LinkType = 'link';
export const linkType: LinkType = 'link';

export interface LinkElement extends BaseElement {
  children: (AlineText | TabElement)[];
  href: string;
  type: LinkType;
  suggestedEdits?: SuggestedLinkEdit[] | null;
}

export type LinkProps = Omit<LinkElement, 'children'>;

/**
 * A *fake* Element that we add to types so we can construct "fake" nodes (e.g., for use in functions like Node.nodes).
 * Remove when Slate supports completely generic types.  See https://github.com/ianstormtaylor/slate/pull/4177.
 */
export interface FakeElement<Child = AlineDescendant> {
  children: Child[];
  type: 'fake';
  suggestedEdits?: SuggestedEdit[] | null;
}

// TODO: duplicate of XmlPageNumberingFormat, think of a better way to handle this
export type PageNumberingFormat =
  | 'decimal'
  | 'numberInDash'
  | 'upperLetter'
  | 'lowerLetter'
  | 'upperRoman'
  | 'lowerRoman';

export type PageNumberingConfig = {
  format: PageNumberingFormat;
  start: number;
};

export interface HeaderFooterProps {
  diffFirstPage: boolean;
  evenOdd: boolean;
  pageNumbering?: PageNumberingConfig;
}

export type AlineMarkerElement = BookmarkMarkerElement | SuggestionMarkerElement;
export type AlineInlineElement =
  | LinkElement
  | InputFieldElement
  | CannedFieldElement
  | BreakElement
  | CrossReferenceElement
  | DrawingElement
  | DynamicInlineElement
  | TabElement
  | AlineMarkerElement
  | InlineInstructionFieldElement
  | CheckboxElement
  | CheckboxOptionFieldElement;
export type ParagraphChildElement = AlineInlineElement | AlineText;
export type AlineBlockElement =
  | DocumentElement
  | DynamicBlockElement
  | FakeElement
  | FooterElement
  | FootersElement
  | HeaderElement
  | HeadersElement
  | ListElement
  | ParagraphElement
  | TableElement
  | TableRowElement
  | TableCellElement
  | BlockInstructionFieldElement;
export type AlineContentBlock = ParagraphElement | TableElement | DynamicBlockElement | TOCElement;
export type AlineElement = AlineBlockElement | AlineInlineElement;

export type PopperNode =
  | InputFieldElement
  | DynamicBlockElement
  | LinkElement
  | DrawingElement
  | InlineInstructionFieldElement;
export type DynamicElement = DynamicBlockElement | InputFieldElement;
export type LocationIdElement = AlineContentBlock | TableRowElement | TableCellElement;
